import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Popover, PopoverAnchor, PopoverContent } from './popover';

const OPTION = 'button:not([disabled])';

export function SuggestionPopover({
  open,
  onDismiss,
  anchor,
  children,
  className,
  anchorClassName,
}: {
  open: boolean;
  onDismiss: () => void;
  anchor: ReactNode;
  children: ReactNode;
  className?: string;
  anchorClassName?: string;
}) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const options = () => [...(contentRef.current?.querySelectorAll<HTMLButtonElement>(OPTION) ?? [])];

  const onAnchorKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' || !open) return;
    const first = options()[0];
    if (!first) return;
    event.preventDefault();
    first.focus();
  };

  const onListKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const list = options();
    const at = list.indexOf(document.activeElement as HTMLButtonElement);
    event.preventDefault();
    if (event.key === 'ArrowUp' && at <= 0) {
      anchorRef.current?.querySelector<HTMLInputElement>('input')?.focus();
      return;
    }
    list[Math.min(list.length - 1, Math.max(0, at + (event.key === 'ArrowDown' ? 1 : -1)))]?.focus();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
    >
      <PopoverAnchor asChild>
        <div ref={anchorRef} onKeyDown={onAnchorKey} className={cn('relative', anchorClassName)}>
          {anchor}
        </div>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="start"
        sideOffset={4}
        onKeyDown={onListKey}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (anchorRef.current?.contains(event.target as Node)) event.preventDefault();
        }}
        className={cn('w-[var(--radix-popover-trigger-width)] overflow-hidden p-0', className)}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}
